// chat/photo.tsx — the camera button in the chat composer, sibling of the mic.
//
// Bogdan 2026-07-09: "should there be a way to add photos through the chat? same button for take
// photo / upload photo, next to the microphone." The composer carries a photo just like it carries
// dictation. The model NEVER sees the image — a photo attached here is filed on-device onto one of
// the user's workouts (e.g. a form or progress shot). No billed brain call is needed to file a photo.

import { useRef, type ChangeEvent, type CSSProperties } from "react";
import { Camera } from "lucide-react";
import { readDownscaledPhoto } from "../photo";

/** Twin of the mic button in ./voice.tsx — same 40px pill, so the two sit as a pair. */
const ICON_BTN: CSSProperties = {
  flexShrink: 0, width: 40, height: 40, borderRadius: 999, padding: 0, cursor: "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  border: "1px solid var(--border, #d9d4ea)", background: "var(--elev-2, #ffffff)",
  color: "var(--ink-1, #1f1b2e)", fontSize: 18, lineHeight: 1,
  transition: "background .15s, color .15s, box-shadow .15s, border-color .15s",
};

export function PhotoButton({ onPhoto, disabled }: { onPhoto: (dataUrl: string) => void; disabled?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);

  async function onPicked(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // let the same file be picked twice in a row
    if (!file) return;
    try { onPhoto(await readDownscaledPhoto(file)); } catch { /* the composer shows nothing changed */ }
  }

  return (
    <>
      {/* accept=image/* gives iOS the native "Take Photo / Photo Library" sheet — one button, both. */}
      <input ref={ref} type="file" accept="image/*" onChange={(e) => void onPicked(e)} style={{ display: "none" }} />
      <button type="button" style={ICON_BTN} aria-label="Add a photo" title="Take or choose a photo"
        disabled={disabled} onClick={() => ref.current?.click()}>
        <Camera size={18} />
      </button>
    </>
  );
}

/** The little "photo attached" strip above the composer, so the photo is never invisible. */
export function PhotoChip({ src, onClear }: { src: string; onClear: () => void }) {
  return (
    <div className="row" style={{ gap: "0.5rem", alignItems: "center", padding: "0.4rem 0.6rem" }}>
      <img src={src} alt="" style={{ width: 34, height: 34, objectFit: "cover", borderRadius: "var(--radius-sm)" }} />
      <span className="muted" style={{ fontSize: "var(--fs-xs)", flex: 1 }}>
        Photo attached — name the workout, or just send for your latest.
      </span>
      <button type="button" className="link" style={{ fontSize: "var(--fs-xs)" }} onClick={onClear}>Remove</button>
    </div>
  );
}
